import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef } from 'react';
import { css } from '@emotion/react';
import styled from '../../styles/styled';
import { deprecate } from '../../util/logger';
const COLOR_MAP = {
    confirm: {
        text: 'white',
        background: 'g700',
    },
    notify: {
        text: 'bodyColor',
        background: 'y300',
    },
    alert: {
        text: 'white',
        background: 'r500',
    },
    neutral: {
        text: 'bodyColor',
        background: 'n200',
    },
    promo: {
        text: 'white',
        background: 'v500',
    },
};
const baseStyles = ({ theme }) => css `
  border-radius: ${theme.borderRadius.pill};
  display: inline-block;
  padding: 2px ${theme.spacings.byte};
  font-size: 14px;
  line-height: 20px;
  font-weight: ${theme.fontWeight.bold};
  text-align: center;
  letter-spacing: 0.25px;
`;
const variantStyles = ({ theme, variant = 'neutral', }) => {
    // TODO: remove the legacy variants and this switch statement in v5
    /* eslint-disable no-param-reassign */
    switch (variant) {
        case 'success':
            if (process.env.NODE_ENV !== 'production' &&
                process.env.NODE_ENV !== 'test') {
                deprecate('Badge', "The Badge's `success` variant is deprecated.", 'Use the `confirm` variant instead.');
            }
            variant = 'confirm';
            break;
        case 'warning':
            if (process.env.NODE_ENV !== 'production' &&
                process.env.NODE_ENV !== 'test') {
                deprecate('Badge', "The Badge's `warning` variant is deprecated.", 'Use the `notify` variant instead.');
            }
            variant = 'notify';
            break;
        case 'danger':
            if (process.env.NODE_ENV !== 'production' &&
                process.env.NODE_ENV !== 'test') {
                deprecate('Badge', "The Badge's `danger` variant is deprecated.", 'Use the `alert` variant instead.');
            }
            variant = 'alert';
            break;
        default:
            break;
    }
    /* eslint-enable no-param-reassign */
    const currentColor = COLOR_MAP[variant];
    return css `
    background-color: ${theme.colors[currentColor.background]};
    color: ${theme.colors[currentColor.text]};
  `;
};
const isDynamicWidth = (children) => {
    if (typeof children === 'string') {
        return children.length > 2;
    }
    return false;
};
const circleStyles = ({ circle = false, children }) => circle &&
    css `
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 4px;
    height: 24px;
    width: ${isDynamicWidth(children) ? 'auto' : '24px'};
  `;
const StyledBadge = styled('div')(baseStyles, variantStyles, circleStyles);
/**
 * A badge communicates the status of an element or the count of items
 * related to an element.
 */
export const Badge = forwardRef((props, ref) => (_jsx(StyledBadge, { ref: ref, ...props }, void 0)));
Badge.displayName = 'Badge';
